import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import { SummaryFlag, useUpdateSummaryFlagMutation } from 'generated/graphql';
import capitalize from 'lodash/capitalize';
import { UUID } from 'v2/core/entities/uuid';
import { createAnalyticsTracker } from 'v2/analytics/events';

const trackFlagSummaryEvents = createAnalyticsTracker('flagSummary');

interface UpdateSummaryFlagOptions {
  feedbackID: UUID;
  summaryFlagType: SummaryFlag;
  keywordSummaryHash?: string;
  initialFlag?: SummaryFlag;
  keywordSummaryText?: string;
}

export const useUpdateSummaryFlag = ({
  feedbackID,
  summaryFlagType,
  keywordSummaryHash,
  initialFlag,
  keywordSummaryText,
}: UpdateSummaryFlagOptions) => {
  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const flaggedSummaryRecords = useFeedbackOnFeedbackStore(
    ({ flaggedSummaryRecords }) => flaggedSummaryRecords,
  );
  const flaggedRecord = flaggedSummaryRecords.find(
    (record) =>
      record.id === feedbackID &&
      record.keywordSummaryHash === keywordSummaryHash,
  );
  const isFlagged = flaggedRecord
    ? flaggedRecord.summaryFlagType === summaryFlagType
    : initialFlag === summaryFlagType;

  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const getFlagSummarySuccessToastMessage = ({
    summaryFlagType,
    isFlagged,
  }: {
    summaryFlagType: SummaryFlag;
    isFlagged: boolean;
  }) => {
    if (isFlagged) {
      return `Removed "${summaryFlagType}" flag from summary`;
    }

    return `Summary flagged as "${summaryFlagType}". Thanks for your feedback! We'll use it to improve the quality of summaries.`;
  };

  const getEventName = ({
    isFlagged,
    summaryFlagType,
  }: {
    isFlagged: boolean;
    summaryFlagType: SummaryFlag;
  }) => {
    if (summaryFlagType === SummaryFlag.Incorrect) {
      return isFlagged ? 'Removed Incorrect flag' : 'Flagged as Incorrect';
    }

    return isFlagged ? 'Removed Missing flag' : 'Flagged as Missing';
  };

  const [updateSummaryFlag, { loading }] = useUpdateSummaryFlagMutation({
    onCompleted: () => {
      if (isFlagged) {
        dispatch({
          type: FeedbackOnFeedbackActionType.REMOVE_FLAG_FROM_RECORD_SUMMARY,
          payload: {
            id: feedbackID,
            keywordSummaryHash,
          },
        });
      } else {
        dispatch({
          type: FeedbackOnFeedbackActionType.SET_FLAG_ON_RECORD_SUMMARY,
          payload: {
            id: feedbackID,
            summaryFlagType,
            keywordSummaryHash,
          },
        });
      }
      showSuccessNotification({
        contentComponent: getFlagSummarySuccessToastMessage({
          summaryFlagType,
          isFlagged,
        }),
        props: {},
      });
      trackFlagSummaryEvents({
        eventName: getEventName({ isFlagged, summaryFlagType }),
        feedbackID,
        keywordSummaryText,
      });
    },
    onError: () => {
      showErrorNotification({
        contentComponent: `Failed to ${
          isFlagged ? 'unflag' : 'flag'
        } summary as ${capitalize(summaryFlagType)}`,
        props: {},
      });
    },
  });

  return { updateSummaryFlag, isLoading: loading };
};
